/// <reference types="google.maps" />
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  NgZone,
  ViewEncapsulation,
  forwardRef,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { PostalAddressI } from '@bodyanalytics/data-models-ui';
import { GoogleMapsLoaderService } from '../../services/google-maps-loader/google-maps-loader.service';

/**
 * Street-address input with Google Places Autocomplete (US, `types: address`).
 *
 * The component IS the street-line form control (ControlValueAccessor), so the
 * parent keeps its `address1` / `address` `FormControl` and every validator on
 * it (`safeAddressCharactersValidator`, `maxLength`) keeps running unchanged —
 * the parent template only swaps `<input formControlName="address1">` for
 * `<bodyanalytics-address-autocomplete formControlName="address1">` and keeps
 * its own label / error markup.
 *
 * When the member picks a suggestion the street line is written into the
 * control and {@link placeSelected} emits the parsed `{ street, city, state,
 * zip }` so the parent can `patchValue` its sibling city/state/zip controls.
 *
 * Graceful degradation (fail-open, by design): no `mapsApiKey` in ENV, script
 * blocked, or Places unavailable → {@link autocompleteReady} emits `false` and
 * the field behaves as a plain text input. The admin app (no maps key/CSP) gets
 * exactly today's behaviour.
 *
 * Uses the legacy `google.maps.places.Autocomplete` widget rather than the
 * `PlaceAutocompleteElement` web component so the input keeps the host form's
 * classes/tokens and no CUSTOM_ELEMENTS_SCHEMA is needed; `fields` is limited
 * to `address_components` (Basic Data billing tier).
 *
 * A11y: the legacy widget adds no ARIA to the input it decorates, so once
 * Places is attached the input is exposed as `role="combobox"` with
 * `aria-autocomplete="list"`, `aria-haspopup="listbox"`, `aria-controls` → the
 * widget's own `.pac-container` (given an id) and `aria-expanded` mirrored from
 * that container's inline `display` via a MutationObserver. Nothing is added
 * in the degraded (plain input) state.
 */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'bodyanalytics-address-autocomplete',
  templateUrl: './address-autocomplete.component.html',
  styleUrls: ['./address-autocomplete.component.scss'],
  // The Places dropdown renders outside the component (body-level `.pac-container`).
  encapsulation: ViewEncapsulation.None,
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => AddressAutocompleteComponent), multi: true }],
})
export class AddressAutocompleteComponent implements ControlValueAccessor, AfterViewInit {
  /** `id` of the inner input — pair with the parent's `<label for>`. */
  readonly inputId = input('address1');
  /** CSS class(es) for the inner input so it inherits the host form's look (`abx-input`, `form-control`). */
  readonly cssClass = input('abx-input');
  readonly placeholder = input('');
  readonly maxlength = input(120);
  readonly required = input(false);
  readonly autocompleteAttr = input('address-line1');
  /** Parent-computed error state (drives `is-invalid` + `aria-invalid`). */
  readonly invalid = input(false);
  /** `aria-describedby` target when invalid (the parent's error element id). */
  readonly describedBy = input<string | null>(null);

  /** Parsed address when a Places suggestion is chosen. */
  readonly placeSelected = output<PostalAddressI>();
  /** `true` once Places is attached, `false` when the field degraded to plain text. */
  readonly autocompleteReady = output<boolean>();

  protected readonly value = signal('');
  protected readonly disabled = signal(false);
  protected readonly ready = signal(false);
  /** Whether the Places dropdown (`.pac-container`) is currently shown — drives `aria-expanded`. */
  protected readonly expanded = signal(false);
  /** Id given to the widget's own `.pac-container` once discovered — `aria-controls` target. */
  protected readonly listboxId = signal<string | null>(null);

  private readonly inputEl = viewChild.required<ElementRef<HTMLInputElement>>('input');
  private readonly loader = inject(GoogleMapsLoaderService);
  private readonly ngZone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);

  private onChange: (value: string) => void = () => undefined;
  private onTouched: () => void = () => undefined;
  private autocomplete: google.maps.places.Autocomplete | null = null;
  private preexistingContainers = new Set<Element>();
  private ownContainer: HTMLElement | null = null;
  private pacObserver: MutationObserver | null = null;

  ngAfterViewInit(): void {
    void this.attach();
  }

  // ── ControlValueAccessor ────────────────────────────────────────────────
  writeValue(value: string | null): void {
    this.value.set(value ?? '');
  }
  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  // ── DOM ─────────────────────────────────────────────────────────────────
  protected handleInput(event: Event): void {
    const raw = (event.target as HTMLInputElement).value;
    this.value.set(raw);
    this.onChange(raw);
    this.syncDropdownState();
  }

  protected handleFocus(): void {
    this.syncDropdownState();
  }

  protected handleBlur(): void {
    this.onTouched();
    this.syncDropdownState();
  }

  /** Enter inside the pac dropdown selects a suggestion — do not submit the form. */
  protected handleKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && this.ready() && this.isDropdownOpen()) {
      event.preventDefault();
    }
    this.syncDropdownState();
  }

  private isDropdownOpen(): boolean {
    if (this.ownContainer) return this.ownContainer.style.display !== 'none';
    return !!document.querySelector('.pac-container:not([style*="display: none"])');
  }

  /**
   * The widget appends its `.pac-container` lazily (first keystroke) and toggles
   * it with an inline `display`; there is no event for it. Discover ours once,
   * then mirror its visibility into `aria-expanded` through a MutationObserver.
   */
  private syncDropdownState(): void {
    if (!this.ready()) return;
    if (!this.ownContainer) {
      const found = Array.from(document.querySelectorAll<HTMLElement>('.pac-container')).find(
        node => !this.preexistingContainers.has(node),
      );
      if (!found) return;
      this.ownContainer = found;
      const id = found.id || `${this.inputId()}-pac-listbox`;
      found.id = id;
      this.listboxId.set(id);
      if (typeof MutationObserver !== 'undefined') {
        this.pacObserver = new MutationObserver(() => this.ngZone.run(() => this.updateExpanded()));
        this.pacObserver.observe(found, { attributes: true, attributeFilter: ['style'], childList: true });
      }
    }
    this.updateExpanded();
  }

  private updateExpanded(): void {
    this.expanded.set(this.isDropdownOpen());
  }

  private async attach(): Promise<void> {
    const ok = await this.loader.load({ libraries: ['places'] });
    if (!ok || typeof google === 'undefined' || !google.maps?.places?.Autocomplete) {
      this.autocompleteReady.emit(false);
      return;
    }
    const el = this.inputEl().nativeElement;
    // The widget appends its own `.pac-container` to <body>; remember what was
    // there before so ours can be removed on destroy (else every mount leaks one).
    const preexisting = new Set<Element>(Array.from(document.querySelectorAll('.pac-container')));
    this.preexistingContainers = preexisting;
    const autocomplete = new google.maps.places.Autocomplete(el, {
      componentRestrictions: { country: 'us' },
      types: ['address'],
      fields: ['address_components'],
    });
    this.autocomplete = autocomplete;
    autocomplete.addListener('place_changed', () => this.ngZone.run(() => this.onPlaceChanged()));
    this.destroyRef.onDestroy(() => {
      this.pacObserver?.disconnect();
      this.pacObserver = null;
      google.maps.event?.clearInstanceListeners?.(autocomplete);
      google.maps.event?.clearInstanceListeners?.(el);
      document.querySelectorAll('.pac-container').forEach(node => {
        if (!preexisting.has(node)) node.remove();
      });
      this.ownContainer = null;
      this.autocomplete = null;
    });
    this.ready.set(true);
    this.autocompleteReady.emit(true);
  }

  private onPlaceChanged(): void {
    const place = this.autocomplete?.getPlace();
    const parsed = parsePlace(place?.address_components);
    if (!parsed) {
      // No structured result (user pressed Enter on free text) — keep the raw value.
      const raw = this.inputEl().nativeElement.value;
      this.value.set(raw);
      this.onChange(raw);
      return;
    }
    this.value.set(parsed.street);
    this.inputEl().nativeElement.value = parsed.street;
    this.onChange(parsed.street);
    this.onTouched();
    this.placeSelected.emit(parsed);
  }
}

/** Google `address_components` → `{ street, city, state, zip }`; `null` when no street. */
export function parsePlace(components: google.maps.GeocoderAddressComponent[] | undefined): PostalAddressI | null {
  if (!components?.length) return null;
  const find = (type: string, short = false): string => {
    const c = components.find(comp => comp.types.includes(type));
    return c ? (short ? c.short_name : c.long_name) : '';
  };
  const streetNumber = find('street_number');
  const route = find('route');
  const street = [streetNumber, route].filter(Boolean).join(' ').trim();
  if (!street) return null;
  const city = find('locality') || find('sublocality_level_1') || find('postal_town') || find('neighborhood');
  const state = find('administrative_area_level_1', true).toUpperCase();
  const zip = find('postal_code').slice(0, 5);
  return { street, city, state, zip };
}
